import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/macro';
import type { I18n } from '@lingui/core';
import { AddressesService } from '@/api/services/AddressesService';
import type { Address } from '@/types/address';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const getAddressSchema = (i18n: I18n) => z.object({
  street: z.string().min(1, i18n._(msg`Street is required`)),
  houseNumber: z.string().max(20).optional(),
  info2: z.string().optional(),
  city: z.string().min(1, i18n._(msg`City is required`)),
  zip: z.string().min(1, i18n._(msg`Postal code is required`)),
  countryIso: z.string().min(2, i18n._(msg`Country is required`)).max(2),
});

type AddressFormData = z.infer<ReturnType<typeof getAddressSchema>>;

interface AddressFormProps {
  onClose: () => void;
  onSuccess: (address: Address) => void;
}

// Common country codes for Europe
const COUNTRIES = [
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'PT', name: 'Portugal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'AT', name: 'Austria' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IE', name: 'Ireland' },
];

export function AddressForm({ onClose, onSuccess }: AddressFormProps) {
  const { t, i18n } = useLingui();
  const queryClient = useQueryClient();

  const addressSchema = useMemo(() => getAddressSchema(i18n), [i18n]);

  const form = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      street: '',
      houseNumber: '',
      info2: '',
      city: '',
      zip: '',
      countryIso: 'CH', // Default to Switzerland
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: AddressFormData) => {
      return AddressesService.postApiV1Addresses({
        street: data.street,
        houseNumber: data.houseNumber || undefined,
        info2: data.info2 || undefined,
        city: data.city,
        zip: data.zip,
        countryIso: data.countryIso,
      });
    },
    onSuccess: (newAddress) => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
      toast.success(t`Address created successfully`);
      onSuccess(newAddress);
    },
    onError: () => {
      toast.error(t`Failed to create address`);
    },
  });

  const onSubmit = (values: AddressFormData) => {
    createMutation.mutate(values);
  };

  return (
    <Dialog open onOpenChange={(open) => {
      // Only close on user interaction (ESC key or X button)
      if (!open) {
        onClose();
      }
    }}>
      <DialogContent 
        className="sm:max-w-[500px]" 
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t`address.form.title.create`}</DialogTitle>
          <DialogDescription>
            {t`Enter the address details for your childcare location`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit(onSubmit)(e);
            }} 
            className="space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <FormField
                control={form.control}
                name="street"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormLabel>{t`address.form.fields.street`}</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="street-address"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="houseNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t`address.form.fields.houseNumber`}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="info2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`address.form.label.addressLine2`}</FormLabel>
                  <FormControl>
                    <Input 
                      autoComplete="address-line2"
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>
                    {t`Apartment, suite, unit, building, floor, etc. (optional)`}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="zip"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t`address.form.label.postalCode`}</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="postal-code"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t`City`}</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="address-level2"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="countryIso"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Country`}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
              >
                {t({ id: "common.cancel", message: "Cancel" })}
              </Button>
              <Button 
                type="submit" 
                disabled={createMutation.isPending}
                onClick={(e) => {
                  // Prevent event bubbling
                  e.stopPropagation();
                }}
              >
                {createMutation.isPending ? t`address.form.status.creating` : t`address.form.action.create`}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}